/**
 * Orchestrator: test print, and full print (PDF → PDF.js → bitmapToMono → commands → BLE).
 * Web Bluetooth only; no Capacitor.
 */

import { writePayload } from './chunkWriter';
import { buildTestPayload, buildRasterPayload } from './phomemoCommands';
import { bitmapToMono } from './bitmapToMono';
import { renderPdfToImageData } from './pdfToBitmap';
import type { LabelSpec, PrinterProtocol } from './types';

const PT_PER_INCH = 72;

export interface TestPrintOptions {
  density?: number;
  /** Printer language; only ESC/POS is implemented for now. */
  protocol?: PrinterProtocol;
  onProgress?: (chunkIndex: number, totalChunks: number) => void;
  signal?: AbortSignal;
}

/** Run a minimal test print (init + form feed). */
export async function testPrint(options: TestPrintOptions = {}): Promise<void> {
  const { density, onProgress, signal } = options;
  const payload = buildTestPayload(density);
  await writePayload(payload, { onProgress, signal });
}

export interface PrintLabelOptions {
  pdfBase64: string;
  density?: number;
  barcodeSafeMode?: boolean;
  /** Printer language; only ESC/POS is implemented for now. */
  protocol?: PrinterProtocol;
  margin?: number;
  widthPx?: number;
  heightPx?: number;
  /** When provided, overrides margin/widthPx/heightPx and dpi for rasterization. */
  labelSpec?: LabelSpec;
  onProgress?: (chunkIndex: number, totalChunks: number) => void;
  signal?: AbortSignal;
}

/**
 * Full print: PDF (base64) → PDF.js render → bitmapToMono → commands → chunkWriter.
 * If labelSpec is provided, widthPx/heightPx/margin/dpi are derived from it.
 */
export async function printLabel(options: PrintLabelOptions): Promise<void> {
  const {
    pdfBase64,
    density = 50,
    barcodeSafeMode = true,
    labelSpec,
    onProgress,
    signal,
  } = options;

  let widthPx: number;
  let heightPx: number;
  let marginPx: number;
  let dpi: number;

  if (labelSpec) {
    dpi = labelSpec.dpi;
    widthPx = Math.round(labelSpec.widthIn * dpi);
    heightPx = Math.round(labelSpec.heightIn * dpi);
    marginPx = Math.round(labelSpec.marginPt * (dpi / PT_PER_INCH));
  } else {
    dpi = 203;
    widthPx = options.widthPx ?? 812;
    heightPx = options.heightPx ?? 1218;
    marginPx = options.margin ?? 0;
  }

  const { data, width, height } = await renderPdfToImageData(pdfBase64, {
    widthPx,
    heightPx,
    margin: marginPx,
    dpi,
  });

  const { raster, widthBytes, height: monoHeight } = bitmapToMono(data, width, height, {
    barcodeSafeMode,
    margin: 0,
  });

  const payload = buildRasterPayload(raster, widthBytes, monoHeight, density);
  await writePayload(payload, { onProgress, signal });
}
